import React, { useEffect, useMemo } from 'react';
import { graphql } from 'gatsby';

import { useTranslation } from 'react-i18next';
import useWithGA from '../hooks/useWithGA';
import {
  createUrlPath,
  getArrayItemsToStringNice,
  isCustomer,
} from '../utils/utils';
import useTransformGatsbyData from '../hooks/useTransformGatsbyData';
import useSearch from '../hooks/useSearch';
import useNavigation from '../hooks/useNavigation';
import useStrapiPageData from '../hooks/data/useStrapiPageData';
import useGetCategoryData from '../hooks/data/useGetCategoryData';

import {
  getLogoAssetObject,
  sortCategoriesByTierOrName,
} from '../utils/sharedFunc';
import MetatagsForLayout from '../components/MetatagsForLayout';

import { isLocaleSweden } from '../utils/locale';

import { Robot } from '../../static/icons/robot';

import CONSTANTS_NUMBERS from '../constants/numbers';

import { PAGE_SYSTEMGUIDE } from '../constants/strapi';

import { SystemAlternatives } from '../ui/templates/systemAlternatives/SystemAlternatives';

export const pageQuery = graphql`
  query ComparisonTemplate(
    $categories: [String]!
    $pageBuildId: String!
    $systemId: String!
    $systemsToCompareIds: [String]!
  ) {
    compareCategories: allBwCategory(filter: { id: { in: $categories } }) {
      nodes {
        data
      }
    }
    bwPageBuildData(id: { eq: $pageBuildId }) {
      data
    }
    bwComparedSystem: bwSystem(id: { eq: $systemId }) {
      data
    }
    bwComparedSystems: allBwSystem(
      filter: { id: { in: $systemsToCompareIds } }
    ) {
      nodes {
        data
      }
    }
    bwStrapiPageSystemAlternative: bwSinglePage(
      id: { eq: "page-system-alternative" }
    ) {
      data
    }
    bwStrapiPageSystem: bwSinglePage(id: { eq: "page-system" }) {
      data
    }
    bwStrapiPageCompare: bwSinglePage(id: { eq: "page-compare-system" }) {
      data
    }
  }
`;
/**
   bwPageSystemguide: bwSinglePage(id: { eq: "page-systemguide" }) {
        data
      }
    }  
 */

const AlternativesToSystem = ({ location, pageContext, data }) => {
  const search = useSearch();

  const {
    compareCategories,
    bwStrapiPageSystemAlternative,
    bwStrapiPageSystem,
    bwStrapiPageCompare,
    // bwPageSystemguide,
    bwPageBuildData,
    bwComparedSystem,
  } = data;

  const { siteUrls } = bwPageBuildData.data.pageData;

  const { menu, footer } = useNavigation(siteUrls, location);
  const { trackOutboundLink, savePageView, handleDataLayer } = useWithGA();
  const { t } = useTranslation();
  const { locale, systemId } = pageContext;

  const comparedSystems = useTransformGatsbyData(data.bwComparedSystems);

  const {
    // title,
    subTitle,
    categoryTitle: strapiCategoryTitle,
  } = bwStrapiPageSystemAlternative?.data;

  const allCategories = useTransformGatsbyData(compareCategories);
  const allCompareCategories = allCategories;
  // const strapiPageSystemguide = bwPageSystemguide?.data;
  const strapiPageSystem = bwStrapiPageSystem?.data;
  const strapiPageCompare = bwStrapiPageCompare?.data;

  const isSweden = isLocaleSweden(locale);

  const mainSystem = bwComparedSystem.data;

  useEffect(() => {
    savePageView({ type: 'SYSTEM_ALTERNATIVE', ids: [systemId] });
  }, []);

  const { defaultCategory, systemSiteDataForLocale } = mainSystem;

  const {
    systemName: comparedSystemName,
    systemNameUrl: comparedSystemNameUrl,
    companyName,
    customerType,
    mediaAssets,
  } = systemSiteDataForLocale;

  const customer = isCustomer(customerType);

  const { getCategoryData, categoryName, categoryUrl } =
    useGetCategoryData(defaultCategory);

  const sortedCategories = sortCategoriesByTierOrName(allCompareCategories);

  const categoriesArrayNice = getArrayItemsToStringNice(
    sortedCategories.map((cat) => cat.categoryName),
    t
  );

  const topThreeSystemsLinks = getArrayItemsToStringNice(
    comparedSystems.slice(0, 3).map((system) => {
      const systemUrl = createUrlPath([
        strapiPageSystem.buildData.slug,
        system.systemSiteDataForLocale.systemNameUrl,
      ]);

      return `<a href="${systemUrl}">${system.systemSiteDataForLocale.systemName}</a>`;
    }),
    t
  );

  const categoryTitle = strapiCategoryTitle.replaceAll(
    '{system}',
    comparedSystemName
  );

  const pageSubTitle = subTitle
    .replaceAll('{system}', comparedSystemName)
    .replaceAll('{categories}', categoriesArrayNice)
    .replaceAll('{topSystems}', topThreeSystemsLinks);

  const systemsToIdMemo = useMemo(
    () =>
      comparedSystems.reduce((acc, system) => {
        acc[system.id] = system;
        return acc;
      }, {}),
    [comparedSystems]
  );

  const logoAsset = getLogoAssetObject(mediaAssets);

  const [systemguidePageUrl] = useStrapiPageData(PAGE_SYSTEMGUIDE);

  const urlToGuide = systemguidePageUrl;

  const categoriesSystemsComponentData = sortedCategories
    .map((category) => {
      const systems = comparedSystems.filter((systemToCompare) =>
        systemToCompare.categories.includes(category.categoryDbName)
      );

      return { category, systems };
    })
    .filter(
      (categorySystemsToCompare) => categorySystemsToCompare.systems.length > 0
    )
    .map((categorySystemsToCompare) => {
      const { categoryHasGuide: hasGuide, categoryUrl: compareCategoryUrl } =
        getCategoryData(categorySystemsToCompare.category.categoryDbName);

      const systemGuideHref = `/${compareCategoryUrl}${urlToGuide}`;

      const breakPoint = 12;
      const alternativeSystems = categorySystemsToCompare.systems.map(
        (system) => {
          const systemWithData = systemsToIdMemo[system.id];
          const { systemSiteDataForLocale: systemSiteDataForLocaleItem } =
            systemWithData;
          const {
            systemName,
            systemNameUrl,
            mediaAssets: mediaAssetsLocalItem,
            customerType,
          } = systemSiteDataForLocaleItem;
          const systemLogoAsset = getLogoAssetObject(mediaAssetsLocalItem);

          const rating = systemWithData?.ratingBW;
          const description = t('systemCard.learnMore').replaceAll(
            '{systemName}',
            systemName
          );

          const systemsCompareUrls = [
            {
              id: mainSystem.id,
              systemNameUrl: mainSystem.systemSiteDataForLocale?.systemNameUrl,
            },
            {
              id: system.id,
              systemNameUrl: system.systemSiteDataForLocale?.systemNameUrl,
            },
          ]
            .sort((a, b) => a.id.localeCompare(b.id))
            .map((sys) => sys.systemNameUrl)
            .join('-vs-');

          const compareHref = createUrlPath([
            strapiPageCompare.buildData.slug,
            systemsCompareUrls,
          ]);

          const showMoreHref = createUrlPath([
            strapiPageSystem.buildData.slug,
            systemNameUrl,
          ]);
          return {
            id: `${categorySystemsToCompare.category.categoryName}-${system.id}`,
            name: systemName,
            imgUrl: `https://ik.imagekit.io/businesswith/tr:w-100,h-100,cm-pad_resize,dpr-2/logo/${systemLogoAsset?.url}`,
            rating,
            description,
            compareHref,
            showMoreHref,
            isCustomer: isCustomer(customerType),
          };
        }
      );

      let hideAlternativeSystems = alternativeSystems
        .filter((alternative) => !alternative?.isCustomer)
        .sort((a, b) => a.name.localeCompare(b.name));

      let showAlternativeSystems = alternativeSystems
        .filter((alternative) => alternative?.isCustomer)
        .sort((a, b) => b.rating - a.rating);

      if (showAlternativeSystems.length < breakPoint) {
        const moveItemsCount = breakPoint - showAlternativeSystems.length;
        const moveItems = hideAlternativeSystems.slice(0, moveItemsCount);

        showAlternativeSystems.push(...moveItems);
        hideAlternativeSystems = hideAlternativeSystems.slice(moveItemsCount);
      }

      if (hideAlternativeSystems?.length <= 3) {
        const moveItems = hideAlternativeSystems.slice(0, 1);

        showAlternativeSystems.push(...moveItems);
        hideAlternativeSystems = [];
      }

      if (
        showAlternativeSystems.length % 3 != 0 &&
        hideAlternativeSystems.length > 0
      ) {
        const moveItemsCount = 3 - (showAlternativeSystems.length % 3);
        const moveItems = hideAlternativeSystems.slice(0, moveItemsCount);

        showAlternativeSystems.push(...moveItems);
        hideAlternativeSystems = hideAlternativeSystems.slice(moveItemsCount);
      }

      return {
        id: `alternatives-${categorySystemsToCompare.category.categoryName}`,
        categoryDbName: categorySystemsToCompare?.category?.categoryDbName,
        title: categoryTitle.replace(
          '{category}',
          categorySystemsToCompare.category.categoryName
        ),
        categoryName: categorySystemsToCompare.category.categoryName,
        hasGuide,
        systemGuideHref,
        alternatives: showAlternativeSystems,
        hiddenAlternatives: hideAlternativeSystems,
      };
    });
  const hero = {
    description: pageSubTitle,
    companyName,
  };
  return (
    <SystemAlternatives
      sections={[
        {
          sectionType: 'single',
          component: {
            type: 'header',
            componentData: {
              title: t('sp.alternativesTitle', {
                systemName: comparedSystemName,
              }),
              subTitle: companyName,
              rating: mainSystem?.ratingBW,
              imgUrl: `https://ik.imagekit.io/businesswith/tr:w-200,h-100,cm-pad_resize,dpr-2/logo/${logoAsset.url}`,
              reviewButtonHref: '/#', // todo
              websiteButtonHref: systemSiteDataForLocale.systemExternalUrl,
              extraBtnText: systemSiteDataForLocale.extraBtnText,
              extraBtnLink: systemSiteDataForLocale.extraBtnLink,
              card: {
                title: isSweden
                  ? t('common.helpSystemGuide')
                  : t('common.trySystemGuide'),
                description: t('common.systemGuideAlgorithmsText', {
                  number: CONSTANTS_NUMBERS.guideRuns,
                }),
                url: urlToGuide,
                icon: <Robot color="#297FFF" width="5" />,
                handleClick: () => {
                  handleDataLayer({
                    event_name: 'to_systemguide',
                    page_type: 'System alternative page',
                    system_category: defaultCategory || '',
                  });
                  // TODO: Implement this all the way
                  // handleDataLayer({
                  //   event_name: 'cta',
                  //   page_type: 'Category page',
                  //   system_category: category || '',
                  //   type: 'systemguide',
                  // });
                },
              },
              companyName,
            },
          },
        },
        {
          sectionType: 'single',
          component: {
            type: 'alternatives',
            componentData: categoriesSystemsComponentData,
          },
        },
      ]}
      breadcrumbs={[
        {
          title: t('common.home'),
          href: '/',
        },
        { title: categoryName, href: `/${categoryUrl}/` },
        {
          title: comparedSystemName,
          href: createUrlPath([
            strapiPageSystem.buildData.slug,
            comparedSystemNameUrl,
          ]),
        },
        {
          title: t('sp.alternativesTitle', { systemName: comparedSystemName }),
          href: location?.pathname,
        },
      ]}
      t={t}
      locale={locale}
      hero={hero}
      search={search}
      menu={menu}
      footer={footer}
      customer={customer}
      systemNameUrl={createUrlPath([
        strapiPageSystem.buildData.slug,
        comparedSystemNameUrl,
      ])}
      handleDataLayer={handleDataLayer}
      trackOutboundLink={trackOutboundLink}
    />
  );
};

AlternativesToSystem.propTypes = {};

export default AlternativesToSystem;

export function Head({ data, location, pageContext }) {
  const { t } = useTranslation();
  const { locale } = pageContext;
  const {
    bwStrapiPageSystemAlternative,
    bwPageBuildData,
    bwComparedSystem,
    bwComparedSystems,
    compareCategories,
  } = data;

  const strapiPageSystemAlternative = bwStrapiPageSystemAlternative?.data;
  const pageData = bwPageBuildData?.data?.pageData;

  const { seo } = strapiPageSystemAlternative;
  const { siteUrls } = pageData;

  const mainSystem = bwComparedSystem.data;
  const comparedSystemName = mainSystem?.systemSiteDataForLocale?.systemName;
  const year = new Date().getFullYear();

  const allCategories = useTransformGatsbyData(compareCategories);
  const comparedSystems = useTransformGatsbyData(bwComparedSystems);
  const allCompareCategories = allCategories;

  const sortedCategories = sortCategoriesByTierOrName(allCompareCategories);

  const categoriesArrayNice = getArrayItemsToStringNice(
    sortedCategories.map((cat) => cat.categoryName),
    t
  );

  const topSystems = getArrayItemsToStringNice(
    comparedSystems
      .slice(0, 3)
      .map((system) => system.systemSiteDataForLocale.systemName),
    t
  );

  const seoTitle = seo?.metaTitle
    .replaceAll('{system}', comparedSystemName)
    .replaceAll('{year}', year);

  const seoDescrition = seo?.metaDescription
    .replaceAll('{system}', comparedSystemName)
    .replaceAll('{categories}', categoriesArrayNice)
    .replaceAll('{topSystems}', topSystems);

  return (
    <MetatagsForLayout
      title={seoTitle}
      description={seoDescrition}
      pathname={location.pathname}
      locale={locale}
      siteUrls={siteUrls}
    />
  );
}
